/**
 * Which store the storefront serves.
 *
 * In production the hostname decides. On `{subdomain}.{platform domain}` or a
 * merchant's own domain (`www.quickprepai.com`) the page's own hostname is sent
 * as `X-Storefront-Host`, because the API lives on a different host and its
 * `Host` header says nothing about the shop.
 *
 * The dashboard is a different hostname, so a "Preview" link from it would
 * otherwise resolve to whatever the dashboard is served from and 404 with
 * STORE_NOT_FOUND. Preview links therefore carry `?store={subdomain}`, which is
 * remembered for the rest of the browser session (so navigating around the
 * storefront keeps working) and sent as `X-Storefront-Host`.
 *
 * `VITE_STOREFRONT_HOST` stays supported as the build-time default for a dev
 * server pinned to one store.
 */
const STORAGE_KEY = 'neon_preview_store';

export const STORE_PARAM = 'store';

function readSession(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be blocked; preview then lasts a single page load.
    return null;
  }
}

function rememberSession(subdomain: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, subdomain);
  } catch {
    // As above - not worth failing the request over.
  }
}

/** Stores are served at {subdomain}.{this}; mirrors the backend setting. */
export const PLATFORM_STOREFRONT_DOMAIN = (
  import.meta.env.VITE_PLATFORM_STOREFRONT_DOMAIN ?? 'neoncarts.com'
).toLowerCase();

/** Subdomains of the platform domain that are NeonCarts itself, not a shop. */
const PLATFORM_SUBDOMAINS = new Set(['www', 'app', 'api', 'cdn']);

/**
 * The page's hostname when it is a shop's own address
 * (`metro-clinic.neoncarts.com`), otherwise null.
 */
export function storeSubdomainHost(
  pageHostname: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.hostname,
): string | null {
  if (!pageHostname) return null;
  const hostname = pageHostname.toLowerCase();
  const suffix = `.${PLATFORM_STOREFRONT_DOMAIN}`;
  if (!hostname.endsWith(suffix)) return null;
  const subdomain = hostname.slice(0, -suffix.length);
  if (!subdomain || subdomain.includes('.') || PLATFORM_SUBDOMAINS.has(subdomain)) return null;
  return hostname;
}

/** Cloudflare Pages' own hostnames: deploy previews of this app, never a shop. */
const PREVIEW_DEPLOY_SUFFIX = '.pages.dev';

/**
 * The page's hostname when it is a shop's own address - its platform
 * subdomain or a custom domain the merchant connected - otherwise null.
 *
 * A custom domain is anything that is not NeonCarts' own hostname, a dev
 * server or a deploy preview: every other name only reaches this bundle
 * because a merchant pointed it here. Which shop it belongs to is the API's
 * answer, from the hostname sent with each request.
 */
export function shopOwnHost(
  pageHostname: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.hostname,
): string | null {
  if (!pageHostname) return null;
  const subdomainHost = storeSubdomainHost(pageHostname);
  if (subdomainHost) return subdomainHost;

  const hostname = pageHostname.toLowerCase();
  if (isPlatformHost(hostname) || !hostname.includes('.')) return null;
  if (hostname.endsWith(PREVIEW_DEPLOY_SUFFIX)) return null;
  // Deeper names under the platform domain (`a.b.neoncarts.com`) are not shops.
  if (hostname.endsWith(`.${PLATFORM_STOREFRONT_DOMAIN}`)) return null;
  // An IP address is a server being reached directly, not a shop.
  if (/^[\d.]+$/.test(hostname) || hostname.includes(':')) return null;
  return hostname;
}

/** The store to ask for, or null to let the Host header decide. */
export function resolveStorefrontHost(): string | null {
  if (typeof window === 'undefined') return null;

  // On a shop's own address the address is the answer - a `?store=` there
  // must not show a different shop under this one's name.
  const ownHost = shopOwnHost();
  if (ownHost) return ownHost;

  const requested = new URLSearchParams(window.location.search).get(STORE_PARAM);
  if (requested) {
    rememberSession(requested);
    return requested;
  }

  return readSession() ?? import.meta.env.VITE_STOREFRONT_HOST ?? null;
}

/** Where the shop a shopper walks around is mounted. */
export const LIVE_STORE_ROOT = '/store';

/**
 * The same shop, mounted for a merchant looking at it from the dashboard.
 *
 * A second address rather than a flag on the first because of installed apps.
 * Every shop's app claims `/store` as its scope, and outside production every
 * shop shares one origin - so once one shop is installed, Chrome opens any
 * `/store…` link in that app's window, whichever shop the link names (a scope
 * matches the path, never the query string). A merchant previewing their
 * second shop would land inside their first. Nothing is installed under
 * `/preview/store`, so a preview always opens as an ordinary tab.
 */
export const PREVIEW_STORE_ROOT = '/preview/store';

function isUnder(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/** Whether `pathname` is a storefront page, at either mount. */
export function isStorefrontPath(pathname: string): boolean {
  return isUnder(pathname, LIVE_STORE_ROOT) || isUnder(pathname, PREVIEW_STORE_ROOT);
}

/** Whether `pathname` is the dashboard's preview of a shop. */
export function isPreviewStorePath(pathname: string): boolean {
  return isUnder(pathname, PREVIEW_STORE_ROOT);
}

/**
 * A dashboard link into the storefront.
 *
 * `path` is written against the live mount (`/store/products/x`) and comes out
 * under the preview one, so the link opens in a browser tab rather than in an
 * installed shop app (see `PREVIEW_STORE_ROOT`).
 *
 * Without a subdomain the store is left for the storefront to resolve, which is
 * the right thing in production and honestly fails in development when no store
 * exists yet.
 */
export function storefrontUrl(path: string, subdomain?: string | null): string {
  const previewPath = isUnder(path, LIVE_STORE_ROOT)
    ? `${PREVIEW_STORE_ROOT}${path.slice(LIVE_STORE_ROOT.length)}`
    : path;
  return subdomain
    ? `${previewPath}?${STORE_PARAM}=${encodeURIComponent(subdomain)}`
    : previewPath;
}

/** A store's platform hostname: `{subdomain}.{platform domain}`. */
export function storeHostname(subdomain: string): string {
  return `${subdomain}.${PLATFORM_STOREFRONT_DOMAIN}`;
}

/**
 * The shop's public address, as its customers reach it.
 *
 * `customDomain` should only be passed once that domain actually serves the
 * shop (routing and certificate both in place); otherwise the platform
 * subdomain, which always does.
 */
export function liveStorefrontUrl(subdomain: string, customDomain?: string | null): string {
  return `https://${customDomain ?? `${subdomain}.${PLATFORM_STOREFRONT_DOMAIN}`}/`;
}

/**
 * Whether this page is NeonCarts' own site rather than somebody's shop.
 *
 * True on the platform domain, its reserved subdomains and a dev server; false
 * on a shop's subdomain and on a merchant's custom domain, both of which serve
 * the same bundle.
 */
export function isPlatformHost(
  pageHostname: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.hostname,
): boolean {
  const hostname = (pageHostname ?? '').toLowerCase();
  return (
    !hostname ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === PLATFORM_STOREFRONT_DOMAIN ||
    [...PLATFORM_SUBDOMAINS].some((sub) => hostname === `${sub}.${PLATFORM_STOREFRONT_DOMAIN}`)
  );
}

/**
 * Whether this page may offer to install the shop as an app.
 *
 * Only on the shop's own address: its subdomain, its custom domain, or a dev
 * server (where every shop shares `localhost`, so the install flow can still be
 * tried). Never on the dashboard's preview mount, and never on NeonCarts' own
 * hostnames - a shop installed from `app.neoncarts.com/store` would be an app
 * scoped to the dashboard's origin, and every other shop previewed there would
 * open inside it.
 */
export function shopInstallable(
  pathname: string = typeof window === 'undefined' ? '' : window.location.pathname,
  pageHostname: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.hostname,
): boolean {
  if (isPreviewStorePath(pathname)) return false;
  const hostname = (pageHostname ?? '').toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  return !isPlatformHost(hostname);
}

/**
 * Where "Go to NeonCarts" leads from a page that has no shop to show.
 *
 * On NeonCarts' own hostnames (and a dev server) the platform is this origin, so
 * the root is enough. Anywhere else - a shop's subdomain, a merchant's custom
 * domain - the root is the very address that just failed, so it has to be the
 * platform domain spelled out.
 */
export function platformHomeUrl(
  pageHostname: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.hostname,
): string {
  return isPlatformHost(pageHostname) ? '/' : `https://${PLATFORM_STOREFRONT_DOMAIN}/`;
}

/** What the API says when a shop has renamed away from the address asked for. */
export interface StoreMove {
  /** The shop's address now: its custom domain, else `{subdomain}.{platform}`. */
  new_host: string;
  /** Its new platform subdomain, for preview links that name the store. */
  subdomain: string;
}

type PageLocation = Pick<Location, 'hostname' | 'pathname' | 'search' | 'hash'>;

/**
 * Where a page should go after the API answered `STORE_MOVED`, or null.
 *
 * Only a storefront page moves. On the shop's own (old) hostname that is the
 * same path on the new host - a printed QR code or a shared product link still
 * lands where it meant to. On a preview (`/store?store=old`) the store named in
 * the address is swapped for the new one. Anywhere else - the dashboard - a
 * stale name is the page's own business, and navigating away would lose work.
 */
export function storeMoveTarget(
  move: StoreMove,
  page: PageLocation | undefined = typeof window === 'undefined' ? undefined : window.location,
): string | null {
  if (!page) return null;
  const tail = `${page.pathname}${page.search}${page.hash}`;
  if (shopOwnHost(page.hostname)) {
    return page.hostname === move.new_host ? null : `https://${move.new_host}${tail}`;
  }
  if (!isStorefrontPath(page.pathname)) return null;
  const params = new URLSearchParams(page.search);
  if (params.get(STORE_PARAM) === move.subdomain) return null;
  params.set(STORE_PARAM, move.subdomain);
  return `${page.pathname}?${params.toString()}${page.hash}`;
}

let moving = false;

/** Follow a `STORE_MOVED` answer, once, if this page is a storefront. */
export function followStoreMove(details: Record<string, unknown> | null): void {
  const newHost = details?.new_host;
  const subdomain = details?.subdomain;
  if (moving || typeof newHost !== 'string' || typeof subdomain !== 'string') return;
  const target = storeMoveTarget({ new_host: newHost, subdomain });
  if (!target) return;
  moving = true;
  // A remembered preview would otherwise name the old store on the next load.
  if (readSession() !== null) rememberSession(subdomain);
  window.location.replace(target);
}
